
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import LocationGroupSection from './LocationGroupSection';
import ChipChipDiscountChart from './ChipChipDiscountChart';
import { BenchmarkData } from '@/services/benchmarkDataService';
import { useChipChipOrders } from '@/hooks/useChipChipOrders';

interface BenchmarkDataTableProps {
  data: BenchmarkData;
  comparisonType: 'min' | 'avg';
  frequency: 'daily' | 'weekly' | 'monthly';
  isLoading: boolean;
  productName?: string;
  dateFrom?: string;
  dateTo?: string;
}

const BenchmarkDataTable: React.FC<BenchmarkDataTableProps> = ({ 
  data, 
  comparisonType, 
  frequency,
  isLoading,
  productName,
  dateFrom,
  dateTo
}) => {
  const hasChipChipData = data.locationGroups.some(group => 
    group.groupName === 'ChipChip Sales' || group.groupName === 'ChipChip Sales (Database)' || group.groupName === 'ChipChip Sales (API)'
  );

  // Fetch ChipChip order data independently with frequency aggregation
  const { orderData, isLoading: orderLoading } = useChipChipOrders(
    productName || '',
    dateFrom || '',
    dateTo || '',
    frequency
  );
  const handleExportCSV = () => {
    if (!data.locationGroups.length) return;

    const csvData = [];
    csvData.push(['Group', 'Date', 'Min Price', 'Avg Price', 'Detailed Records'].join(','));

    data.locationGroups.forEach(group => {
      group.dates.forEach((date, index) => {
        const detailedCount = group.detailedData[index]?.length || 0;
        csvData.push([
          group.groupName,
          date,
          group.minPrices[index]?.toFixed(2) || '0',
          group.avgPrices[index]?.toFixed(2) || '0',
          detailedCount
        ].join(','));
      });
    });

    const blob = new Blob([csvData.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chipchip-price-benchmark-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex-1 p-2 h-full w-full">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading benchmark data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data.locationGroups.length) {
    return (
      <div className="flex-1 p-2 h-full w-full">
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">No data available for the selected filters.</p>
          <p className="text-gray-500 text-sm mt-2">Try adjusting your date range or product selection.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto h-full w-full">
      <div className="p-2 w-full">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-red-600">
            ChipChip Price Benchmark Comparison{productName ? ` for ${productName}` : ''}
          </h1>
          <Button onClick={handleExportCSV} variant="outline" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>

        <div className="space-y-4 w-full">
          {data.locationGroups.map((groupData, index) => (
            <LocationGroupSection
              key={index}
              data={groupData}
              comparisonType={comparisonType}
            />
          ))}
          
          <ChipChipDiscountChart 
            data={data} 
            comparisonType={comparisonType} 
            frequency={frequency}
            orderData={orderData}
            productName={productName}
          />
        </div>
      </div>
    </div>
  );
};

export default BenchmarkDataTable;
